import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_BANNER_ACTIVE,
  BASE_URL_FOR_ELASTIC_SEARCH
} from '../../../Constants/Config';
import { waitForElasticConnection } from '../../../hooks/checkElasticConnection.js';
import { defaultTracyLatLng } from '../../../utils';
import Cookies from 'js-cookie';

const initialState = {
  loading: false,
  bannerList: [],
  error: '',
  lastFetched: null,
  lastFetchedStoreType: '',
};

// Generate pending, fulfilled and rejected action types
export const fetchActiveBanners = createAsyncThunk(
  'banner/fetchActiveBanners',
  async (data = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { banner } = state;
      const { forceRefresh = false } = data;
      const normalizedStoreType =
        data?.storeType === 'pickup' ? 'pickup' : data?.storeType || '';
      
      // Check if we have cached data and it's fresh (less than 5 minutes old)
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
      const now = Date.now();
      
      if (
        !forceRefresh &&
        banner.bannerList.length > 0 &&
        banner.lastFetched &&
        banner.lastFetchedStoreType === normalizedStoreType &&
        (now - banner.lastFetched) < CACHE_DURATION
      ) {
        return banner.bannerList; // Return cached data
      }

      
      // Check elastic connection
      const isElasticConnected = await waitForElasticConnection(getState);
      const baseUrl = isElasticConnected
        ? BASE_URL_FOR_ELASTIC_SEARCH
        : BASE_URL_SANDBOX;

      // Get location from cookies or use default
      const storedLocation = Cookies.get('userLocation');
      let location = null;
      try {
        location = storedLocation
          ? JSON.parse(decodeURIComponent(storedLocation))
          : defaultTracyLatLng;
      } catch (e) {
        location = defaultTracyLatLng;
      }

      // Create FormData for the request
      const formData = new FormData();
      formData.append('lat', location?.lat || '');
      formData.append('lng', location?.lng || '');
      formData.append('store', normalizedStoreType);

      const response = await axios.post(
        `${BASE_URL_SANDBOX}${GET_BANNER_ACTIVE}`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          // Add timeout to prevent hanging requests
          timeout: 10000
        }
      );

      if (response.data.status === 'Success') {
        return response.data.result || [];
      } else {
        return rejectWithValue({
          status: response.data.status,
          message: response.data.msg || 'Failed to fetch banners'
        });
      }
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || 'Network error occurred'
      });
    }
  },
);

const bannerSlice = createSlice({
  name: 'banner',
  initialState,
  reducers: {
    // Action to clear banner data if needed
    clearBannerData: (state) => {
      state.bannerList = [];
      state.error = '';
      state.lastFetched = null;
      state.lastFetchedStoreType = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchActiveBanners.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchActiveBanners.fulfilled, (state, action) => {
      state.loading = false;
      state.bannerList = action.payload || [];
      state.error = '';
      state.lastFetched = Date.now(); // Set timestamp when data was fetched
      state.lastFetchedStoreType = action.meta.arg?.storeType === 'pickup' ? 'pickup' : action.meta.arg?.storeType || '';
    });
    builder.addCase(fetchActiveBanners.rejected, (state, action) => {
      state.loading = false;
      // Don't clear existing data on error, keep what we have
      state.error = action.payload?.message || action.error?.message || 'Failed to fetch banners';
    });
  },
});

export const { clearBannerData } = bannerSlice.actions;
export default bannerSlice.reducer;
